import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import Select from "components/common/Select";
import PhoneInput from "components/creation/PhoneInput";
import { Device } from "protogen/phone_service_pb";
import Checkbox from "components/common/Checkbox";
import { Phone } from "lucide-react";

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (phoneNumber: string, device: number, transcribe: boolean) => void;
  loading: boolean;
  isMobile: boolean;
  phoneTeaser: string;
};

export default ({
  open,
  onClose,
  onSubmit,
  loading,
  isMobile,
  phoneTeaser,
}: Props) => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [callDevice, setCallDevice] = useState<number>(
    isMobile ? Device.PHONE : Device.CLIENT,
  );
  const [transcribe, setTranscribe] = useState<boolean>(true);

  const handleSubmit = () => {
    onSubmit(phoneNumber, callDevice, transcribe);
    setPhoneNumber("");
  };

  const handleClose = () => {
    onClose();
    setPhoneNumber("");
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Start a Call</DialogTitle>
      <DialogContent>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          sx={{ padding: "20px" }}
        >
          <Typography
            variant="body"
            sx={{ marginBottom: "16px", display: "block" }}
          >
            Enter the phone number you'd like to call:
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <PhoneInput
              autoFocus
              fullWidth
              value={phoneNumber}
              onChange={(value) => setPhoneNumber(value as string)}
              sx={{
                input: {
                  marginTop: "0 !important",
                },
              }}
            />
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }} disabled={loading}>
            <Select
              variant="detachedOutlined"
              value={callDevice}
              disabled={loading || isMobile}
              onChange={(e: SelectChangeEvent<number>) =>
                setCallDevice(e.target.value as number)
              }
              fullWidth
            >
              <MenuItem value={Device.CLIENT}>Here</MenuItem>
              <MenuItem value={Device.PHONE}>Cell {phoneTeaser}</MenuItem>
            </Select>
            <FormHelperText>
              <Typography variant="bodySmall" component="span">
                {callDevice === Device.CLIENT
                  ? "This call will be made from the browser / app."
                  : "This call will be connected with your personal cell phone number."}
              </Typography>
            </FormHelperText>
          </FormControl>
          <Box>
            <FormControlLabel
              label="Transcribe call"
              disabled={loading}
              sx={{ userSelect: "none" }}
              control={
                <Checkbox
                  disabled={loading}
                  checked={transcribe}
                  onChange={() => setTranscribe((t) => !t)}
                />
              }
            />
            <Typography
              variant="bodySmall"
              color="text.secondary"
              sx={{ marginLeft: "28px" }}
            >
              The call will be transcribed to collect notes and summarization.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 2,
              mt: 2,
            }}
          >
            <Box display="flex" gap={2} alignSelf="flex-end">
              <Button onClick={handleClose} variant="outlined">
                Cancel
              </Button>
              <Button
                color="success"
                onClick={handleSubmit}
                disabled={!phoneNumber.trim() || loading}
                startIcon={<Phone size={16} />}
              >
                Call
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
